/**
 * Data Preview Panel
 * Painel lateral para exibir dados estruturados extraídos via OCR
 */

import React from 'react';
import { Copy, FileText, User, Building2, Phone, MapPin, Calendar, Eye, EyeOff } from 'lucide-react';
import { maskCPF, maskPhoneBR } from '@/utils/masks';
import { useAIDocDataStore } from '@/store/aiDataStore';
import { useSidebar } from '@/components/ui/sidebar';

interface StructuredData {
  document?: {
    type: string;
    name: string;
  };
  identification?: {
    fullName: string;
    cpf: string;
    rg: string;
    cnh: string;
  };
  issuer?: {
    org: string;
    uf: string;
  };
  contacts?: {
    phone: string;
    email: string;
  };
  address?: {
    line: string;
    district: string;
    city: string;
    state: string;
    zip: string;
  };
  dates?: {
    birth: string;
    issue: string;
    expiry: string;
  };
  rawText?: string;
}

interface DataPreviewPanelProps {
  open: boolean;
  onClose: () => void;
  data: StructuredData | null;
  onInsert: (value: string) => void;
  documentId?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const DataPreviewPanelComponent: React.FC<DataPreviewPanelProps> = ({
  open,
  onClose,
  data,
  onInsert,
  documentId = 'current',
  onMouseEnter,
  onMouseLeave
}) => {
  const [showRawText, setShowRawText] = React.useState(true);
  const [copiedText, setCopiedText] = React.useState<string | null>(null);
  
  // ✅ Seletor reativo do Zustand - re-renderiza quando version muda
  const extracts = useAIDocDataStore(s => documentId ? s.extractsByDoc[documentId] : undefined);
  const { loadOCR } = useAIDocDataStore();
  
  // Detectar largura da sidebar para posicionamento correto
  const sidebar = useSidebar();
  const sidebarWidth = sidebar?.state === 'expanded' ? 256 : 48;

  // Carregar dados OCR do DB quando abrir o painel
  React.useEffect(() => {
    if (open && documentId) {
      const loadData = async () => {
        const { data: { user } } = await import('@/integrations/supabase/client').then(m => m.supabase.auth.getUser());
        if (user?.id) {
          await loadOCR(documentId, user.id);
        }
      };
      loadData();
    }
  }, [open, documentId, loadOCR]);

  // Debug logs only when necessary - inside useEffect to avoid loop
  React.useEffect(() => {
    if (open) {
      console.log('🖼️ DataPreviewPanel opened:', { hasData: !!data, documentId });
    }
  }, [open, data, documentId]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  const handleInsert = (value: string, event?: React.MouseEvent) => {
    // CORREÇÃO 3: Inserção em um único clique
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (value && value.trim()) {
      // Force focus back to editor before insertion
      const editor = document.querySelector('.ProseMirror, [contenteditable="true"]') as HTMLElement;
      if (editor) {
        editor.focus();
        // Small delay to ensure focus is established
        setTimeout(() => {
          onInsert(value.trim());
        }, 10);
      } else {
        onInsert(value.trim());
      }
    }
  };

  const DataItem: React.FC<{
    label: string;
    value: string;
    icon?: React.ReactNode;
    formatted?: boolean;
  }> = ({ label, value, icon, formatted = false }) => {
    if (!value) return null;

    const displayValue = formatted ? value : value;
    const isCopied = copiedText === value;

    return (
      <div className="group flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {icon && <span className="text-gray-500 dark:text-gray-400 text-sm">{icon}</span>}
          <div className="min-w-0 flex-1">
            <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
            <div className="text-sm text-gray-900 dark:text-gray-100 truncate" title={displayValue}>
              {displayValue}
            </div>
          </div>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => handleCopy(value)}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Copiar"
          >
            <Copy className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => handleInsert(value, e)}
            className="px-2 py-1 text-xs bg-primary text-primary-foreground hover:bg-primary/90 rounded transition-colors"
            title={`Inserir ${label.toLowerCase()}`}
          >
            Inserir
          </button>
        </div>
        {isCopied && (
          <div className="absolute right-0 -top-8 bg-black text-white text-xs px-2 py-1 rounded">
            Copiado!
          </div>
        )}
      </div>
    );
  };

  const Section: React.FC<{
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    isEmpty?: boolean;
  }> = ({ title, icon, children, isEmpty = false }) => {
    if (isEmpty) return null;

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          {icon}
          <span>{title}</span>
        </div>
        <div className="space-y-1 pl-6">
          {children}
        </div>
      </div>
    );
  };

  // Use universal store methods instead of manual fallback
  // ✅ Normalizar dados do store reativo usando useMemo
  const displayData = React.useMemo(() => {
    if (data) return data;
    
    if (!extracts) return null;
    
    const structured = extracts.structured ?? {};
    return {
      rawText: (extracts.rawText ?? '').replace(/^Texto OCR:\s*/i, ''), // remove prefixo
      identification: structured.identification ?? { fullName: '', cpf: '', rg: '', cnh: '' },
      issuer: structured.issuer ?? { org: '', uf: '' },
      contacts: structured.contacts ?? { phone: '', email: '' },
      address: structured.address ?? { line: '', district: '', city: '', state: '', zip: '' },
      dates: structured.dates ?? { birth: '', issue: '', expiry: '' },
      document: structured.document ?? { type: '', name: '' }
    };
  }, [data, extracts]);
  
  const hasRaw = !!extracts?.rawText && extracts.rawText.length > 0;

  // Early returns after all hooks (React Rules of Hooks)
  if (!open) return null;
  if (!displayData) return null;

  const panelContent = (
    <div className="data-preview-panel--dock bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Dados Extraídos
          </h3>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          ✕
        </button>
      </div>

      <div className="space-y-4">
        {/* Documento */}
        <Section
          title="Documento"
          icon={<FileText className="h-4 w-4" />}
          isEmpty={!displayData.document?.type && !displayData.document?.name}
        >
          <DataItem
            label="Tipo"
            value={displayData.document?.type || ''}
            formatted
          />
          <DataItem
            label="Nome"
            value={displayData.document?.name || ''}
            formatted
          />
        </Section>

        {/* Identificação */}
        <Section
          title="Identificação"
          icon={<User className="h-4 w-4" />}
          isEmpty={!displayData.identification?.fullName && !displayData.identification?.cpf && !displayData.identification?.rg && !displayData.identification?.cnh}
        >
          <DataItem
            label="Nome Completo"
            value={displayData.identification?.fullName || ''}
            icon={<User className="h-3 w-3" />}
          />
          <DataItem
            label="CPF"
            value={displayData.identification?.cpf ? maskCPF(displayData.identification.cpf) : ''}
            icon={<FileText className="h-3 w-3" />}
            formatted
          />
          <DataItem
            label="RG"
            value={displayData.identification?.rg || ''}
            icon={<FileText className="h-3 w-3" />}
          />
          <DataItem
            label="CNH"
            value={displayData.identification?.cnh || ''}
            icon={<FileText className="h-3 w-3" />}
          />
        </Section>

        {/* Emissor */}
        <Section
          title="Órgão Emissor"
          icon={<Building2 className="h-4 w-4" />}
          isEmpty={!displayData.issuer?.org && !displayData.issuer?.uf}
        >
          <DataItem
            label="Órgão"
            value={displayData.issuer?.org || ''}
            icon={<Building2 className="h-3 w-3" />}
          />
          <DataItem
            label="UF"
            value={displayData.issuer?.uf || ''}
            icon={<MapPin className="h-3 w-3" />}
          />
        </Section>

        {/* Contatos */}
        <Section
          title="Contatos"
          icon={<Phone className="h-4 w-4" />}
          isEmpty={!displayData.contacts?.phone && !displayData.contacts?.email}
        >
          <DataItem
            label="Telefone"
            value={displayData.contacts?.phone ? maskPhoneBR(displayData.contacts.phone) : ''}
            icon={<Phone className="h-3 w-3" />}
            formatted
          />
          <DataItem
            label="Email"
            value={displayData.contacts?.email || ''}
            icon={<Phone className="h-3 w-3" />}
          />
        </Section>

        {/* Endereço */}
        <Section
          title="Endereço"
          icon={<MapPin className="h-4 w-4" />}
          isEmpty={!displayData.address?.line && !displayData.address?.city && !displayData.address?.state}
        >
          <DataItem
            label="Logradouro"
            value={displayData.address?.line || ''}
            icon={<MapPin className="h-3 w-3" />}
          />
          <DataItem
            label="Bairro"
            value={displayData.address?.district || ''}
            icon={<MapPin className="h-3 w-3" />}
          />
          <DataItem
            label="Cidade"
            value={displayData.address?.city || ''}
            icon={<MapPin className="h-3 w-3" />}
          />
          <DataItem
            label="Estado"
            value={displayData.address?.state || ''}
            icon={<MapPin className="h-3 w-3" />}
          />
          <DataItem
            label="CEP"
            value={displayData.address?.zip || ''}
            icon={<MapPin className="h-3 w-3" />}
          />
        </Section>

        {/* Datas */}
        <Section
          title="Datas"
          icon={<Calendar className="h-4 w-4" />}
          isEmpty={!displayData.dates?.birth && !displayData.dates?.issue && !displayData.dates?.expiry}
        >
          <DataItem
            label="Nascimento"
            value={displayData.dates?.birth || ''}
            icon={<Calendar className="h-3 w-3" />}
          />
          <DataItem
            label="Emissão"
            value={displayData.dates?.issue || ''}
            icon={<Calendar className="h-3 w-3" />}
          />
          <DataItem
            label="Validade"
            value={displayData.dates?.expiry || ''}
            icon={<Calendar className="h-3 w-3" />}
          />
        </Section>

        {/* Texto Bruto */}
        {displayData.rawText && (
          <div className="space-y-2">
            <button
              onClick={() => setShowRawText(!showRawText)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              {showRawText ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span>Texto Bruto</span>
            </button>
            {showRawText && (
              <div className="pl-6">
                <div className="group relative">
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <button
                      onClick={() => handleCopy(displayData.rawText || '')}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors bg-white dark:bg-gray-800 rounded"
                      title="Copiar texto completo"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                  <pre className="ai-rail-raw-text text-gray-700 dark:text-gray-300">{displayData.rawText?.replace(/^Texto OCR:\s*/i, '') || displayData.rawText}</pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Render inline positioned next to rail
  return (
    <div
      className="absolute z-[10001] transition-opacity duration-200"
      data-component="data-preview-panel"
      style={{
        right: '100%',
        marginRight: '8px',
        top: 0,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {panelContent}
    </div>
  );
};

// Memoize to prevent unnecessary re-renders
export const DataPreviewPanel = React.memo(DataPreviewPanelComponent);
export default DataPreviewPanel;